import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { PageHeader, Card, Button, Input, Badge, ConfirmDialog, Dialog } from '@pilot-hub/ui'
import { settingsApi, authApi, api } from '@pilot-hub/api-client'
import { PanoramaDialog, panoramaArtText, type PanoramaInfo } from '@pilot-hub/ui/components/karte/index'
import { User, Lock, Server, Save, RefreshCw, Eye, EyeOff, Edit2, CheckCircle, AlertCircle, Settings as SettingsIcon, Shield, Images } from 'lucide-react'

interface DjiFest {
  cloud_url: string
  mqtt_url: string
  mqtt_user: string
  workspace_id: string
  api_url: string
  ws_url: string
  broker: { erreichbar: boolean; verbunden: boolean }
  fernsteuerung: { benutzer: string; passwort: string }
}

interface Fernsteuerung {
  gateway_sn: string
  aircraft_sn: string | null
  name: string | null
  drohne: string | null
  angemeldet_am: string | null
  zuletzt_gesehen: string | null
}

interface Settings {
  dji_app_id: string
  dji_app_key_set: boolean
  dji_app_key_masked: string
  dji_app_license_set: boolean
  dji_app_license_masked: string
  dji_platform_name: string
  dji_workspace_name: string
  dji_workspace_desc: string
  dji_sync_enabled: boolean
  dji_fest: DjiFest
}

function ProfilTab() {
  const [daten, setDaten] = useState({ benutzername: '', vorname: '', nachname: '', email: '', telefon: '' })
  const [pw, setPw] = useState({ current_password: '', new_password: '', new_password_repeat: '' })
  const [showCurrentPw, setShowCurrentPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)
  const [showRepeatPw, setShowRepeatPw] = useState(false)
  const [laden, setLaden] = useState(true)
  const [speichertDaten, setSpeichertDaten] = useState(false)
  const [speichertPw, setSpeichertPw] = useState(false)
  const [fehlerDaten, setFehlerDaten] = useState<string | null>(null)
  const [erfolgDaten, setErfolgDaten] = useState<string | null>(null)
  const [fehlerPw, setFehlerPw] = useState<string | null>(null)
  const [erfolgPw, setErfolgPw] = useState<string | null>(null)

  const laden_ = useCallback(async () => {
    setLaden(true)
    try {
      const r = await authApi.me()
      const d = r.data as { benutzername?: string; vorname?: string; nachname?: string; email?: string; telefon?: string }
      setDaten({ benutzername: d.benutzername || '', vorname: d.vorname || '', nachname: d.nachname || '', email: d.email || '', telefon: d.telefon || '' })
    } catch (e: any) {
      setFehlerDaten(e?.response?.data?.detail || 'Profildaten konnten nicht geladen werden')
    } finally { setLaden(false) }
  }, [])

  useEffect(() => { laden_() }, [laden_])

  async function datenSpeichern() {
    setSpeichertDaten(true); setFehlerDaten(null); setErfolgDaten(null)
    try {
      await authApi.updateProfile({ vorname: daten.vorname, nachname: daten.nachname, email: daten.email, telefon: daten.telefon })
      setErfolgDaten('Profildaten gespeichert.')
    } catch (e: any) { setFehlerDaten(e?.response?.data?.detail || 'Speichern fehlgeschlagen') }
    finally { setSpeichertDaten(false) }
  }

  async function passwortAendern() {
    setFehlerPw(null); setErfolgPw(null)
    if (!pw.current_password || !pw.new_password) { setFehlerPw('Bitte aktuelles und neues Passwort eingeben.'); return }
    if (pw.new_password !== pw.new_password_repeat) { setFehlerPw('Die neuen Passwörter stimmen nicht überein.'); return }
    if (pw.new_password.length < 8) { setFehlerPw('Das neue Passwort muss mindestens 8 Zeichen haben.'); return }
    setSpeichertPw(true)
    try {
      await authApi.changePassword({ current_password: pw.current_password, new_password: pw.new_password, new_password_repeat: pw.new_password_repeat })
      setPw({ current_password: '', new_password: '', new_password_repeat: '' })
      setErfolgPw('Passwort geändert.')
    } catch (e: any) {
      const d = e?.response?.data?.detail
      const meldung = d === 'Current password incorrect' ? 'Das aktuelle Passwort ist falsch.'
        : d === "Passwords don't match" ? 'Die neuen Passwörter stimmen nicht überein.'
        : typeof d === 'string' ? d : 'Passwort konnte nicht geändert werden'
      setFehlerPw(meldung)
    } finally { setSpeichertPw(false) }
  }

  if (laden) return <Card className="p-6"><div className="py-8 text-center text-dark-text-muted">Lade Profil…</div></Card>

  return (
    <div className="space-y-6">
      {/* Meine Daten Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center"><User className="w-5 h-5 text-blue-400" /></div>
          <div>
            <h2 className="text-lg font-semibold text-dark-text">Meine Daten</h2>
            <p className="text-sm text-dark-text-muted">Persönliche Informationen für Benachrichtigungen und Rückfragen</p>
          </div>
        </div>

        {fehlerDaten && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{fehlerDaten}</div>}
        {erfolgDaten && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{erfolgDaten}</div>}

        <div className="space-y-5">
          <div>
            <label className="label flex items-center gap-2"><User className="w-4 h-4 text-dark-text-muted" />Benutzername</label>
            <div className="mt-1.5 rounded-xl border border-dark-border bg-dark-bg-tertiary px-4 py-3 text-dark-text-muted flex items-center gap-3">
              <Input value={daten.benutzername} disabled readOnly className="bg-transparent border-0 focus:ring-0 text-dark-text" />
              <span className="text-xs text-dark-text-subtle">Nicht änderbar</span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Vorname</label>
              <Input value={daten.vorname} onChange={(e) => setDaten({ ...daten, vorname: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Nachname</label>
              <Input value={daten.nachname} onChange={(e) => setDaten({ ...daten, nachname: e.target.value })} className="mt-1.5" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">E-Mail</label>
              <Input type="email" value={daten.email} onChange={(e) => setDaten({ ...daten, email: e.target.value })} placeholder="name@asb.de" className="mt-1.5" />
            </div>
            <div>
              <label className="label">Telefon</label>
              <Input value={daten.telefon} onChange={(e) => setDaten({ ...daten, telefon: e.target.value })} placeholder="z. B. 0151 23456789" className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={datenSpeichern} disabled={speichertDaten} className="w-full sm:w-auto"><Save className="w-4 h-4 mr-2" />{speichertDaten ? 'Speichern…' : 'Speichern'}</Button>
        </div>
      </Card>

      {/* Passwort ändern Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center"><Lock className="w-5 h-5 text-purple-400" /></div>
          <div>
            <h2 className="text-lg font-semibold text-dark-text">Passwort ändern</h2>
            <p className="text-sm text-dark-text-muted">Mindestens 8 Zeichen, sicher und einzigartig</p>
          </div>
        </div>

        {fehlerPw && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{fehlerPw}</div>}
        {erfolgPw && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{erfolgPw}</div>}

        <div className="space-y-5">
          <div>
            <label className="label">Aktuelles Passwort</label>
            <Input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Neues Passwort</label>
              <Input type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Neues Passwort wiederholen</label>
              <Input type="password" autoComplete="new-password" value={pw.new_password_repeat} onChange={(e) => setPw({ ...pw, new_password_repeat: e.target.value })} className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={passwortAendern} disabled={speichertPw} className="w-full sm:w-auto"><Lock className="w-4 h-4 mr-2" />{speichertPw ? 'Ändern…' : 'Passwort ändern'}</Button>
        </div>
      </Card>
    </div>
  )
}

// DJI Cloud API: editierbar sind DJI-Lizenz und Anzeigenamen; MQTT-Verbindung und Adressen sind fest (Server-Konfiguration)
type GeheimKey = 'dji_app_key' | 'dji_app_license'

const DJI_LEER = {
  dji_app_id: '',
  dji_platform_name: '',
  dji_workspace_name: '',
  dji_workspace_desc: '',
  dji_sync_enabled: false,
}

const GEHEIM_LEER: Record<GeheimKey, string> = { dji_app_key: '', dji_app_license: '' }

interface TestErgebnis { name: string; ok: boolean; detail: string }

function Feld({ label, hinweis, children }: { label: string; hinweis?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hinweis && <p className="mt-1.5 text-xs text-dark-text-subtle">{hinweis}</p>}
    </div>
  )
}

function GeheimFeld({ label, hinweis, gesetzt, maske, wert, aendern, mehrzeilig, onAendern, onChange }: {
  label: string; hinweis?: string; gesetzt: boolean; maske: string; wert: string; aendern: boolean; mehrzeilig?: boolean
  onAendern: (an: boolean) => void; onChange: (v: string) => void
}) {
  return (
    <Feld label={label} hinweis={hinweis}>
      {gesetzt && !aendern ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 rounded-xl border border-dark-border bg-dark-bg-tertiary px-4 py-3 font-mono text-sm text-dark-text-muted flex items-center gap-3">
            <span>{maske || '••••••••'}</span>
            <span className="text-xs text-dark-text-subtle">(hinterlegt)</span>
          </div>
          <Button variant="secondary" onClick={() => onAendern(true)}><Edit2 className="w-4 h-4 mr-2" />Ändern</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-3">
          {mehrzeilig ? (
            <textarea value={wert} onChange={e => onChange(e.target.value)} rows={4} autoComplete="off" spellCheck={false}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs" />
          ) : (
            <Input type="password" value={wert} onChange={e => onChange(e.target.value)} autoComplete="new-password" className="flex-1" />
          )}
          {gesetzt && <Button variant="secondary" onClick={() => { onAendern(false); onChange('') }}><EyeOff className="w-4 h-4 mr-2" />Abbrechen</Button>}
        </div>
      )}
    </Feld>
  )
}

function InfoZeile({ label, wert, kopierbar }: { label: string; wert: string; kopierbar?: boolean }) {
  const [kopiert, setKopiert] = useState(false)
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5">
      <div className="w-48 shrink-0 text-sm text-dark-text-muted">{label}</div>
      <div className="flex-1 min-w-0 font-mono text-sm text-dark-text break-all">{wert || '—'}</div>
      {kopierbar && wert && (
        <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard?.writeText(wert).then(() => { setKopiert(true); setTimeout(() => setKopiert(false), 1500) }) }}>
          {kopiert ? 'Kopiert' : 'Kopieren'}
        </Button>
      )}
    </div>
  )
}

function Abschnitt({ titel, beschreibung, children }: { titel: string; beschreibung: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dark-border p-4 space-y-4">
      <div>
        <h3 className="font-semibold text-dark-text">{titel}</h3>
        <p className="text-xs text-dark-text-muted">{beschreibung}</p>
      </div>
      {children}
    </div>
  )
}

function FernsteuerungZugang({ zugang, onNeu }: { zugang: { benutzer: string; passwort: string }; onNeu: () => Promise<void> }) {
  const [zeigen, setZeigen] = useState(false)
  const [bestaetigen, setBestaetigen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  return (
    <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary p-4 space-y-1">
      <div className="text-sm font-medium text-dark-text">Anmeldung auf der Fernsteuerung</div>
      <p className="text-xs text-dark-text-muted">Eigener Zugang nur für die Fernsteuerung (kein Pilot). Die Anmeldeseite merkt sich den Zugang; die Zuordnung zum Piloten erfolgt beim Flug in der Anwendung.</p>
      <InfoZeile label="Benutzername" wert={zugang.benutzer} kopierbar />
      <div className="flex flex-wrap items-center gap-2 py-1.5">
        <div className="w-48 shrink-0 text-sm text-dark-text-muted">Passwort</div>
        <div className="flex-1 min-w-0 font-mono text-sm text-dark-text break-all">{zeigen ? zugang.passwort : '••••••••••••'}</div>
        <Button variant="secondary" size="sm" onClick={() => setZeigen(!zeigen)}>{zeigen ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
        <Button variant="secondary" size="sm" onClick={() => navigator.clipboard?.writeText(zugang.passwort)}>Kopieren</Button>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        {bestaetigen ? (
          <>
            <span className="text-xs text-amber-300">Alle Fernsteuerungen müssen sich danach neu anmelden.</span>
            <Button variant="secondary" size="sm" onClick={() => setBestaetigen(false)}>Abbrechen</Button>
            <Button size="sm" disabled={laeuft} onClick={async () => { setLaeuft(true); try { await onNeu(); setZeigen(true) } finally { setLaeuft(false); setBestaetigen(false) } }}>
              {laeuft ? 'Erzeuge…' : 'Neues Passwort erzeugen'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setBestaetigen(true)}><RefreshCw className="w-4 h-4 mr-2" />Neues Passwort</Button>
        )}
      </div>
    </div>
  )
}

function FernsteuerungenListe() {
  const [liste, setListe] = useState<Fernsteuerung[]>([])
  const [namen, setNamen] = useState<Record<string, string>>({})
  const [gespeichert, setGespeichert] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const r = await api.get('/settings/dji/fernsteuerungen')
      const daten = r.data as Fernsteuerung[]
      setListe(daten)
      setNamen(Object.fromEntries(daten.map(f => [f.gateway_sn, f.name || ''])))
    } catch { /* noch keine Fernsteuerung angemeldet */ }
  }, [])
  useEffect(() => { laden() }, [laden])

  async function speichern(sn: string) {
    await api.put(`/settings/dji/fernsteuerungen/${encodeURIComponent(sn)}`, { name: namen[sn] || '' })
    setGespeichert(sn); setTimeout(() => setGespeichert(null), 1500)
  }

  const zeit = (v: string | null) => v ? new Date(v).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'
  return (
    <div className="rounded-xl border border-dark-border p-4">
      <div className="text-sm font-medium text-dark-text">Angemeldete Fernsteuerungen</div>
      {liste.length === 0 ? (
        <p className="mt-1 text-xs text-dark-text-muted">Noch keine Fernsteuerung angemeldet.</p>
      ) : (
        <>
        <ul className="mt-2 space-y-3 md:hidden">
          {liste.map(f => (
            <li key={f.gateway_sn} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
              <label className="text-xs text-dark-text-subtle" htmlFor={`fs-${f.gateway_sn}`}>Name</label>
              <Input id={`fs-${f.gateway_sn}`} value={namen[f.gateway_sn] || ''} onChange={e => setNamen({ ...namen, [f.gateway_sn]: e.target.value })} placeholder="z. B. Fernsteuerung GW-Drohne" />
              <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-dark-text-subtle">Seriennummer</dt><dd className="break-all font-mono text-xs">{f.gateway_sn}</dd>
                <dt className="text-dark-text-subtle">Drohne</dt><dd>{f.drohne || f.aircraft_sn || '—'}</dd>
                <dt className="text-dark-text-subtle">Zuletzt aktiv</dt><dd>{zeit(f.zuletzt_gesehen || f.angemeldet_am)}</dd>
              </dl>
              <div className="mt-3 flex justify-end border-t border-dark-border pt-3">
                <Button variant="secondary" className="min-h-12" onClick={() => speichern(f.gateway_sn)}>{gespeichert === f.gateway_sn ? 'Gespeichert' : 'Speichern'}</Button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-2 hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-dark-text-muted">
              <th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Seriennummer</th><th className="py-1 pr-3">Drohne</th><th className="py-1">Zuletzt aktiv</th>
            </tr></thead>
            <tbody>
              {liste.map(f => (
                <tr key={f.gateway_sn} className="border-t border-dark-border">
                  <td className="py-1.5 pr-3">
                    <div className="flex items-center gap-2">
                      <Input value={namen[f.gateway_sn] || ''} onChange={e => setNamen({ ...namen, [f.gateway_sn]: e.target.value })} placeholder="z. B. Fernsteuerung GW-Drohne" className="min-w-[200px]" />
                      <Button variant="secondary" size="sm" onClick={() => speichern(f.gateway_sn)}>{gespeichert === f.gateway_sn ? 'Gespeichert' : 'Speichern'}</Button>
                    </div>
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{f.gateway_sn}</td>
                  <td className="py-1.5 pr-3">{f.drohne || f.aircraft_sn || '—'}</td>
                  <td className="py-1.5">{zeit(f.zuletzt_gesehen || f.angemeldet_am)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}

function SystemTab() {
  const [form, setForm] = useState({ ...DJI_LEER })
  const [fest, setFest] = useState<DjiFest | null>(null)
  const [geheim, setGeheim] = useState<Record<GeheimKey, string>>({ ...GEHEIM_LEER })
  const [gesetzt, setGesetzt] = useState<Record<GeheimKey, boolean>>({ dji_app_key: false, dji_app_license: false })
  const [masken, setMasken] = useState<Record<GeheimKey, string>>({ ...GEHEIM_LEER })
  const [aendern, setAendern] = useState<Record<GeheimKey, boolean>>({ dji_app_key: false, dji_app_license: false })
  /** Gespeicherte App ID (gesperrt angezeigt, solange nicht auf „Ändern“ geklickt wurde) */
  const [gespeicherteAppId, setGespeicherteAppId] = useState('')
  const [appIdAendern, setAppIdAendern] = useState(false)
  const [loading, setLoading] = useState(true)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState<string | null>(null)
  const [testLaeuft, setTestLaeuft] = useState(false)
  const [testErgebnisse, setTestErgebnisse] = useState<TestErgebnis[] | null>(null)

  const uebernehmen = useCallback((d: Settings) => {
    setForm({
      dji_app_id: d.dji_app_id || '',
      dji_platform_name: d.dji_platform_name || '',
      dji_workspace_name: d.dji_workspace_name || '',
      dji_workspace_desc: d.dji_workspace_desc || '',
      dji_sync_enabled: !!d.dji_sync_enabled,
    })
    setFest(d.dji_fest)
    setGespeicherteAppId(d.dji_app_id || '')
    setAppIdAendern(false)
    setGesetzt({ dji_app_key: !!d.dji_app_key_set, dji_app_license: !!d.dji_app_license_set })
    setMasken({ dji_app_key: d.dji_app_key_masked || '', dji_app_license: d.dji_app_license_masked || '' })
    setGeheim({ ...GEHEIM_LEER })
    setAendern({ dji_app_key: false, dji_app_license: false })
  }, [])

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const r = await settingsApi.get()
      uebernehmen(r.data as unknown as Settings)
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Einstellungen konnten nicht geladen werden') }
    finally { setLoading(false) }
  }, [uebernehmen])

  useEffect(() => { laden() }, [laden])

  async function speichern() {
    setSpeichert(true); setFehler(null); setErfolg(null)
    try {
      const payload: Record<string, any> = { ...form }
      ;(Object.keys(geheim) as GeheimKey[]).forEach(k => { if (geheim[k].trim()) payload[k] = geheim[k].trim() })
      const r = await settingsApi.update(payload)
      uebernehmen(r.data as unknown as Settings)
      setErfolg('DJI-Einstellungen gespeichert.')
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen') }
    finally { setSpeichert(false) }
  }

  async function verbindungTesten() {
    setTestLaeuft(true); setTestErgebnisse(null)
    try {
      const r = await api.post('/settings/dji/test')
      setTestErgebnisse((r.data as any).ergebnisse as TestErgebnis[])
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Test fehlgeschlagen') }
    finally { setTestLaeuft(false) }
  }

  const set = (k: keyof typeof DJI_LEER) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value })
  const geheimProps = (k: GeheimKey) => ({
    gesetzt: gesetzt[k], maske: masken[k], wert: geheim[k], aendern: aendern[k],
    onAendern: (an: boolean) => setAendern({ ...aendern, [k]: an }),
    onChange: (v: string) => setGeheim({ ...geheim, [k]: v }),
  })

  if (loading) return <Card className="p-6"><div className="py-8 text-center text-dark-text-muted">Lade Einstellungen…</div></Card>

  return (
    <>
      {fehler && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{fehler}</div>}
      {erfolg && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{erfolg}</div>}

      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center"><Server className="w-5 h-5 text-orange-400" /></div>
            <div>
              <h2 className="text-lg font-semibold text-dark-text">DJI Cloud API</h2>
              <p className="text-sm text-dark-text-muted">Anbindung von DJI Pilot 2 an Pilot-Hub2. Einzutragen ist nur die DJI-Lizenz; App Key und License werden nach dem Speichern maskiert.</p>
            </div>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${form.dji_sync_enabled ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-dark-bg-tertiary text-dark-text-muted border border-dark-border'}`}>
            {form.dji_sync_enabled ? 'Anbindung aktiv' : 'Anbindung inaktiv'}
          </span>
        </div>

        <div className="space-y-5">
          <Abschnitt titel="Lizenz (DJI Developer)" beschreibung="Aus der App vom Typ „Cloud API“ im DJI-Entwicklerkonto (developer.dji.com). DJI Pilot 2 prüft damit die Lizenz.">
            <div className="grid gap-4 md:grid-cols-2">
              <Feld label="App ID">
                {gespeicherteAppId && !appIdAendern ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 rounded-xl border border-dark-border bg-dark-bg-tertiary px-4 py-3 font-mono text-sm text-dark-text-muted flex items-center gap-3">
                      <span>{gespeicherteAppId}</span>
                      <span className="text-xs text-dark-text-subtle">(hinterlegt)</span>
                    </div>
                    <Button variant="secondary" onClick={() => setAppIdAendern(true)}><Edit2 className="w-4 h-4 mr-2" />Ändern</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-start gap-3">
                    <Input value={form.dji_app_id} onChange={set('dji_app_id')} placeholder="z. B. 123456" autoComplete="off" className="flex-1" />
                    {gespeicherteAppId && <Button variant="secondary" onClick={() => { setAppIdAendern(false); setForm({ ...form, dji_app_id: gespeicherteAppId }) }}><EyeOff className="w-4 h-4 mr-2" />Abbrechen</Button>}
                  </div>
                )}
              </Feld>
              <GeheimFeld label="App Key" {...geheimProps('dji_app_key')} />
            </div>
            <GeheimFeld label="App License" mehrzeilig hinweis="Vollständigen License-Text aus dem DJI-Entwicklerkonto einfügen." {...geheimProps('dji_app_license')} />
          </Abschnitt>

          <Abschnitt titel="Verbindung für DJI Pilot 2" beschreibung="Fest im System hinterlegt – keine Eingabe nötig. In DJI Pilot 2 unter Cloud-Dienste → Open Platform die Cloud-Dienste-Adresse aufrufen und mit dem Fernsteuerungs-Zugang anmelden.">
            <div className="divide-y divide-dark-border">
              <InfoZeile label="Cloud-Dienste-Adresse" wert={fest?.cloud_url || ''} kopierbar />
              <InfoZeile label="MQTT-Broker" wert={fest?.mqtt_url || ''} />
              <InfoZeile label="MQTT-Benutzer (Gerät)" wert={fest?.mqtt_user || ''} />
              <InfoZeile label="Workspace-ID" wert={fest?.workspace_id || ''} />
              <div className="flex flex-wrap items-center gap-2 py-1.5">
                <div className="w-48 shrink-0 text-sm text-dark-text-muted">Broker-Status</div>
                <span className={`rounded-full px-3 py-0.5 text-xs font-medium border ${fest?.broker.verbunden ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-red-500/10 text-red-300 border-red-500/30'}`}>
                  {fest?.broker.verbunden ? 'verbunden' : fest?.broker.erreichbar ? 'dji-sync nicht mit Broker verbunden' : 'dji-sync nicht erreichbar'}
                </span>
              </div>
            </div>
            {fest?.fernsteuerung && (
              <FernsteuerungZugang zugang={fest.fernsteuerung} onNeu={async () => {
                const r = await api.post('/settings/dji/fernsteuerung-passwort')
                setFest(f => f ? { ...f, fernsteuerung: r.data as { benutzer: string; passwort: string } } : f)
              }} />
            )}
            <FernsteuerungenListe />
          </Abschnitt>

          <Abschnitt titel="Anzeige in DJI Pilot 2" beschreibung="Plattform- und Workspace-Name erscheinen in DJI Pilot 2 in der Cloud-Ansicht.">
            <div className="grid gap-4 md:grid-cols-2">
              <Feld label="Plattformname"><Input value={form.dji_platform_name} onChange={set('dji_platform_name')} placeholder="Pilot-Hub2" autoComplete="off" /></Feld>
              <Feld label="Workspace-Name"><Input value={form.dji_workspace_name} onChange={set('dji_workspace_name')} placeholder="ASB Drohnenstaffel" autoComplete="off" /></Feld>
            </div>
            <Feld label="Workspace-Beschreibung"><Input value={form.dji_workspace_desc} onChange={set('dji_workspace_desc')} autoComplete="off" /></Feld>
          </Abschnitt>

          <div className="rounded-xl border border-dashed border-dark-border p-4">
            <div className="text-sm font-medium text-dark-text">Vorgesehen für spätere DJI-Module (Karten, Wegpunktmissionen)</div>
            <div className="mt-1 divide-y divide-dark-border">
              <InfoZeile label="API-Adresse (HTTPS)" wert={fest?.api_url || ''} />
              <InfoZeile label="WebSocket-Adresse" wert={fest?.ws_url || ''} />
            </div>
          </div>

          <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-dark-text flex items-center gap-2"><RefreshCw className="w-5 h-5 text-dark-text-muted" />DJI-Anbindung aktiv</div>
                <div className="text-xs text-dark-text-muted mt-1">Flug- und Gerätedaten aus DJI Pilot 2 übernehmen</div>
              </div>
              <button type="button" role="switch" aria-checked={form.dji_sync_enabled} onClick={() => setForm({ ...form, dji_sync_enabled: !form.dji_sync_enabled })} className={`relative h-6 w-11 rounded-full transition-colors ${form.dji_sync_enabled ? 'bg-primary-600' : 'bg-dark-border'}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.dji_sync_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {testErgebnisse && (
            <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary px-4 py-3 text-sm space-y-1.5">
              <div className="text-xs text-dark-text-subtle">Prüfung vom Server aus:</div>
              {testErgebnisse.map(t => (
                <div key={t.name} className="flex items-center gap-2">
                  {t.ok ? <CheckCircle className="w-4 h-4 text-green-400" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
                  <span className="font-medium text-dark-text">{t.name}:</span>
                  <span className="text-dark-text-muted">{t.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="secondary" onClick={verbindungTesten} disabled={testLaeuft}><RefreshCw className="w-4 h-4 mr-2" />{testLaeuft ? 'Teste…' : 'Verbindung testen'}</Button>
          <Button onClick={speichern} disabled={speichert}><Save className="w-4 h-4 mr-2" />{speichert ? 'Speichern…' : 'Speichern'}</Button>
        </div>
      </Card>
    </>
  )
}

// --- Fotos & Medien: Speicherstand, Aufbewahrung, Obergrenze, Hotspot-Schwelle (Fotos aus DJI Pilot 2) ---
interface MedienDaten {
  einstellungen: Record<string, number | boolean>
  einheiten: Record<string, string>
  grenzen: Record<string, [number, number]>
  regeln: string[]
  status: {
    belegt_bytes?: number; grenze_bytes?: number; voll?: boolean; host_frei_bytes?: number; host_warn_bytes?: number
    warnungen?: Array<{ code: string; text: string }>; fotos?: number; mit_original?: number; wartend?: number; fehler?: number
    aktualisiert?: string; thermal_sdk?: string
    foto_bytes?: number; video_bytes?: number; ausschnitt_bytes?: number; videos?: number; ausschnitte?: number; aufzeichnung?: boolean
  }
}

interface AusschnittZeile {
  id: number; titel: string; bemerkung: string | null; start: string; dauer_s: number; groesse: number; erstellt_von: string | null
  erstellt_am: string | null; flug_id: number | null; einsatz_id: number | null; flug_nr: string | null; einsatz_titel: string | null
}

// Alle gesicherten Ausschnitte der Videoaufzeichnung (bleiben bis zum Löschen), sortierbar nach Größe oder Alter
interface PanoramaZeile extends PanoramaInfo {
  einsatz_id: number | null; flug_id: number | null; breite_px: number | null; hoehe_px: number | null
  groesse_original: number | null; groesse_verkleinert: number | null; groesse_pano: number
  hat_vorschau: boolean; hochgeladen_am: string | null
}

/** Panoramen der Matrice 4TD (aus DJI Pilot 2): kennzeichnen, filtern, im Viewer öffnen */
function PanoramenListe() {
  const [zeilen, setZeilen] = useState<PanoramaZeile[] | null>(null)
  const [filter, setFilter] = useState<'alle' | 'kugel' | 'breit' | 'vertikal'>('alle')
  const [offen, setOffen] = useState<PanoramaZeile | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const laden = useCallback(async () => {
    try { setZeilen((await api.get('/dji/medien/panoramen')).data as PanoramaZeile[]); setFehler(null) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Panoramen konnten nicht geladen werden') }
  }, [])
  useEffect(() => { laden() }, [laden])
  const sichtbar = (zeilen ?? []).filter(z => filter === 'alle' || z.panorama_art === filter)
  const zeit = (z: string | null) => z ? new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'
  const mb = (b: number | null | undefined) => b ? `${(b / 1048576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : '–'
  const verfuegbar = (z: PanoramaZeile) => z.hat_pano_klein || z.hat_pano_gross || z.hat_verkleinert
  return (
    <div className="space-y-3">
      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{fehler}</div>}
      <div className="flex flex-wrap gap-1" role="group" aria-label="Panorama-Art filtern">
        {([['alle', 'Alle'], ['kugel', 'Kugel 360°'], ['breit', 'Teilpanorama'], ['vertikal', 'Vertikal']] as const).map(([k, t]) => (
          <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
            className={`min-h-[44px] rounded-lg border px-3 text-xs font-semibold ${filter === k ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-muted'}`}>{t}</button>
        ))}
        <button type="button" onClick={laden} className="ml-auto min-h-[44px] rounded-lg border border-dark-border px-3 text-xs text-dark-text-muted"><RefreshCw className="mr-1 inline h-3.5 w-3.5" />Aktualisieren</button>
      </div>
      {zeilen && sichtbar.length === 0 && <p className="text-sm text-dark-text-muted">{zeilen.length === 0 ? 'Noch keine Panoramen hochgeladen.' : 'Keine Panoramen in dieser Auswahl.'}</p>}
      {sichtbar.length > 0 && (
        <ul className="divide-y divide-dark-border rounded-xl border border-dark-border">
          {sichtbar.map(z => (
            <li key={z.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="rounded-full bg-violet-600/25 px-2 py-0.5 text-xs font-semibold text-violet-300">{panoramaArtText(z.panorama_art)}</span>
              <div className="min-w-0 flex-1 basis-48">
                <div className="truncate text-sm text-dark-text">{z.name}</div>
                <div className="text-xs text-dark-text-muted">
                  {zeit(z.aufnahme_zeit ?? z.hochgeladen_am)} · {z.einsatz_titel ?? 'ohne Einsatz'}
                  {z.breite_px && <> · {z.breite_px} × {z.hoehe_px} px</>} · Fassungen {mb(z.groesse_pano)}
                  {z.dateien_geloescht && <> · Bild nicht mehr vorhanden</>}
                </div>
              </div>
              <button type="button" disabled={!verfuegbar(z)} onClick={() => setOffen(z)}
                className="min-h-[44px] rounded-lg border border-primary-500 bg-primary-500/15 px-4 text-sm font-semibold text-dark-text disabled:opacity-40">Öffnen</button>
            </li>
          ))}
        </ul>
      )}
      {offen && <PanoramaDialog pano={offen} onClose={() => setOffen(null)} />}
    </div>
  )
}

function AusschnitteListe({ onGeaendert }: { onGeaendert: () => void }) {
  const [zeilen, setZeilen] = useState<AusschnittZeile[] | null>(null)
  const [sort, setSort] = useState<'alter' | 'groesse'>('alter')
  const [absteigend, setAbsteigend] = useState(true)
  const [loeschen, setLoeschen] = useState<AusschnittZeile | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const laden = useCallback(async () => {
    try { setZeilen((await api.get('/dji/aufnahmen/ausschnitte')).data as AusschnittZeile[]) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Ausschnitte konnten nicht geladen werden') }
  }, [])
  useEffect(() => { laden() }, [laden])
  const sortiert = [...(zeilen ?? [])].sort((a, b) => {
    const d = sort === 'groesse' ? a.groesse - b.groesse : new Date(a.erstellt_am ?? a.start).getTime() - new Date(b.erstellt_am ?? b.start).getTime()
    return absteigend ? -d : d
  })
  const kopf = (id: 'alter' | 'groesse', text: string) => (
    <button type="button" className="font-medium hover:text-dark-text" onClick={() => { if (sort === id) setAbsteigend(!absteigend); else { setSort(id); setAbsteigend(true) } }}>
      {text}{sort === id ? (absteigend ? ' ↓' : ' ↑') : ''}
    </button>)
  async function bestaetigt() {
    if (!loeschen) return
    try { await api.delete(`/dji/aufnahmen/ausschnitt/${loeschen.id}`); setLoeschen(null); await laden(); onGeaendert() }
    catch (e: any) { setLoeschen(null); setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen') }
  }
  return (
    <div className="space-y-2">
      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{fehler}</div>}
      {zeilen && zeilen.length === 0 && <p className="text-sm text-dark-text-muted">Noch keine Ausschnitte gesichert.</p>}
      {sortiert.length > 0 && (
        <>
        <ul className="space-y-3 md:hidden">
          {sortiert.map(a => (
            <li key={a.id} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
              <div className="break-words text-base font-semibold text-dark-text">{a.titel}</div>
              <div className="text-xs text-dark-text-muted">{Math.round(a.dauer_s)} s{a.erstellt_von ? ` · ${a.erstellt_von}` : ''}</div>
              <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-dark-text-subtle">Flug</dt><dd>{a.flug_nr ?? '–'}</dd>
                <dt className="text-dark-text-subtle">Einsatz</dt><dd>{a.einsatz_titel ?? '–'}</dd>
                <dt className="text-dark-text-subtle">Datum</dt><dd>{new Date(a.start).toLocaleString('de-DE')}</dd>
                <dt className="text-dark-text-subtle">Größe</dt><dd>{(a.groesse / 1048576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB</dd>
              </dl>
              <div className="mt-3 flex justify-end border-t border-dark-border pt-3">
                <button type="button" className="min-h-12 rounded-lg border border-dark-border px-4 text-sm text-red-300 hover:bg-dark-bg-tertiary" onClick={() => setLoeschen(a)}>Löschen</button>
              </div>
            </li>))}
        </ul>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-dark-text-muted">
              <tr><th className="py-2 pr-3">Titel</th><th className="pr-3">Flug</th><th className="pr-3">Einsatz</th>
                <th className="pr-3">{kopf('alter', 'Datum')}</th><th className="pr-3 text-right">{kopf('groesse', 'Größe')}</th><th /></tr>
            </thead>
            <tbody>
              {sortiert.map(a => (
                <tr key={a.id} className="border-t border-dark-border">
                  <td className="py-2 pr-3 text-dark-text">{a.titel}<div className="text-xs text-dark-text-muted">{Math.round(a.dauer_s)} s{a.erstellt_von ? ` · ${a.erstellt_von}` : ''}</div></td>
                  <td className="pr-3 text-dark-text-muted">{a.flug_nr ?? '–'}</td>
                  <td className="pr-3 text-dark-text-muted">{a.einsatz_titel ?? '–'}</td>
                  <td className="pr-3 whitespace-nowrap text-dark-text-muted">{new Date(a.start).toLocaleString('de-DE')}</td>
                  <td className="pr-3 text-right tabular-nums text-dark-text-muted">{(a.groesse / 1048576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB</td>
                  <td className="text-right"><button type="button" className="min-h-[36px] rounded-lg border border-dark-border px-3 text-xs text-red-300 hover:bg-dark-bg-tertiary" onClick={() => setLoeschen(a)}>Löschen</button></td>
                </tr>))}
            </tbody>
          </table>
        </div>
        </>)}
      <ConfirmDialog isOpen={!!loeschen} onClose={() => setLoeschen(null)} onConfirm={bestaetigt} title="Ausschnitt löschen?"
        message={`Der gesicherte Ausschnitt „${loeschen?.titel ?? ''}“ (${loeschen ? (loeschen.groesse / 1048576).toFixed(1) : ''} MB) wird endgültig gelöscht.`} confirmText="Löschen" />
    </div>)
}

const MEDIEN_FELDER: Array<{ gruppe: string; beschreibung: string; felder: Array<{ key: string; label: string; hinweis?: string }> }> = [
  { gruppe: 'Speicher', beschreibung: 'Harte Obergrenze für den Medienspeicher auf dem eigenen Server (Foto-Originale, verkleinerte Fotos, Vorschauen und Videoaufnahmen zusammen; gesicherte Ausschnitte zählen nicht mit).',
    felder: [
      { key: 'medien_grenze_gb', label: 'Obergrenze Medienspeicher (Fotos + Videos)', hinweis: 'Bei Erreichen werden zuerst Foto-Originale ohne Einsatz, dann Videos und Foto-Originale von Einsätzen mit Status ABGESCHLOSSEN gelöscht; reicht das nicht, werden Foto-Uploads abgelehnt. Daten laufender Einsätze bleiben unangetastet.' },
      { key: 'medien_host_frei_warn_gb', label: 'Warnung bei weniger freiem Platz auf dem Server', hinweis: 'Warnung, wenn auf dem Server weniger als dieser Wert frei ist.' },
    ] },
  { gruppe: 'Aufbewahrung der Originale und Videos', beschreibung: 'Gilt für Foto-Originale und Videoaufnahmen. Verkleinerte Fotos und Vorschauen folgen der Jahresregel (siehe Regeln); gesicherte Ausschnitte bleiben bis zum Löschen.',
    felder: [
      { key: 'medien_original_tage_nach_einsatz', label: 'Originale und Videos: Tage nach Status ABGESCHLOSSEN' },
      { key: 'medien_original_tage_ohne_einsatz', label: 'Ohne Einsatz: Tage nach Upload bzw. Aufnahme' },
    ] },
  { gruppe: 'Hotspots (IR-Fotos)', beschreibung: 'Ein Bildbereich gilt als Hotspot, wenn er mindestens die Temperaturschwelle erreicht UND mindestens den Abstand über dem Median des Bildes liegt (Umgebung). Gilt für neu verarbeitete Fotos.',
    felder: [
      { key: 'medien_hotspot_schwelle_c', label: 'Temperaturschwelle' },
      { key: 'medien_hotspot_delta_k', label: 'Mindestabstand zur Umgebung (Bildmedian)' },
      { key: 'medien_hotspot_min_pixel', label: 'Kleinste Fläche', hinweis: 'In Bildpunkten des 640×512-Wärmebilds.' },
    ] },
]


// --- Für den Bericht markierte Fotos: Übersicht nach Kategorie und Löschen (nur Admin/Entwickler; das Backend prüft die Rolle) ---
interface BerichtKategorie { key: string; label: string; anzahl: number; bytes: number; loeschbar_anzahl: number; loeschbar_bytes: number; laufend_anzahl: number; laufend_bytes: number }
interface BerichtProtokoll { id: number; nutzer_name: string | null; kategorie: string; anzahl_gewaehlt: number; anzahl_geloescht: number; anzahl_fehler: number; bytes_geloescht: number; medien_ids: number[]; fehler_ids: number[]; ausgefuehrt_am: string }
interface OffenerEinsatz { id: number; titel: string | null; typ: string | null; status: string | null; fotos: number; foto_bytes: number; videos: number; video_bytes: number }
interface BerichtUebersicht { kategorien: BerichtKategorie[]; gesamt: { anzahl: number; bytes: number }; protokoll: BerichtProtokoll[]; offene_einsaetze: OffenerEinsatz[] }
interface LoeschErgebnis { kategorie: string; gewaehlt: number; geloescht: number; fehler: number[]; uebersprungen: number[]; bytes_geloescht: number; speicherstand_aktualisiert: boolean }

const mbText = (b: number) => b < 1048576 ? `${Math.round(b / 1024).toLocaleString('de-DE')} KB` : b >= 1024 ** 3 ? `${(b / 1024 ** 3).toLocaleString('de-DE', { maximumFractionDigits: 2 })} GB` : `${(b / 1048576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
const datumZeit = (z: string) => new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

function BerichtFotosAbschnitt({ onGeaendert }: { onGeaendert: () => void }) {
  const [u, setU] = useState<BerichtUebersicht | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [wahl, setWahl] = useState<string>('')
  const [dialog, setDialog] = useState(false)
  const [eingabe, setEingabe] = useState('')
  const [busy, setBusy] = useState(false)
  const [dialogFehler, setDialogFehler] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<LoeschErgebnis | null>(null)
  const laden = useCallback(async () => {
    try { setU((await api.get('/settings/medien/bericht-fotos')).data as BerichtUebersicht); setFehler(null) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Übersicht konnte nicht geladen werden') }
  }, [])
  useEffect(() => { laden() }, [laden])
  const kat = u?.kategorien.find(k => k.key === wahl) ?? null
  const katText = (key: string) => u?.kategorien.find(k => k.key === key)?.label ?? key
  const oeffnen = () => { setEingabe(''); setDialogFehler(null); setDialog(true) }
  const loeschen = async () => {
    if (!kat) return
    setBusy(true); setDialogFehler(null)
    try {
      const r = (await api.post('/settings/medien/bericht-fotos/loeschen', { kategorie: kat.key, anzahl: Number(eingabe) }, { timeout: 600000 })).data as LoeschErgebnis
      setErgebnis(r); setDialog(false); setWahl('')
      await laden(); onGeaendert()
    } catch (e: any) { setDialogFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); await laden() }
    finally { setBusy(false) }
  }
  const bestaetigt = kat != null && eingabe.trim() === String(kat.loeschbar_anzahl) && kat.loeschbar_anzahl > 0
  return (
    <Abschnitt titel="Für den Bericht markierte Fotos" beschreibung="Markierte Fotos behalten ihre verkleinerten Fassungen, Vorschauen und Panorama-Fassungen über die Jahresregel hinaus (das Original folgt weiter der Frist nach ABGESCHLOSSEN). Hier lassen sie sich je Kategorie endgültig löschen. Nur Fotos abgeschlossener Einsätze (Status ABGESCHLOSSEN) und Fotos ohne Einsatz sind löschbar.">
      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">{fehler}</div>}
      {ergebnis && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${ergebnis.fehler.length ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-green-500/30 bg-green-500/10 text-green-300'}`} role="status" data-testid="bericht-loeschergebnis">
          Kategorie {katText(ergebnis.kategorie)}: {ergebnis.geloescht} von {ergebnis.gewaehlt} Fotos gelöscht ({mbText(ergebnis.bytes_geloescht)} freigegeben).
          {ergebnis.fehler.length > 0 && <> Bei {ergebnis.fehler.length} Foto(s) ist das Löschen fehlgeschlagen (IDs {ergebnis.fehler.join(', ')}); sie bleiben markiert und unverändert und können erneut gelöscht werden.</>}
          {ergebnis.uebersprungen.length > 0 && <> {ergebnis.uebersprungen.length} Foto(s) wurden übersprungen, weil sich Markierung oder Einsatzstatus inzwischen geändert hat (IDs {ergebnis.uebersprungen.join(', ')}).</>}
          {!ergebnis.speicherstand_aktualisiert && <> Der Speicherstand konnte nicht sofort aktualisiert werden und folgt beim nächsten Lauf (alle 5 Minuten).</>}
        </div>
      )}
      {u && (
        <>
          <p className="text-sm text-dark-text-muted" data-testid="bericht-gesamt">Insgesamt {u.gesamt.anzahl} markierte Fotos, Speicherbedarf {mbText(u.gesamt.bytes)} (nur vorhandene Dateien).</p>
          <div className="grid gap-2 md:grid-cols-2" role="radiogroup" aria-label="Kategorie wählen">
            {u.kategorien.map(k => (
              <button key={k.key} type="button" role="radio" aria-checked={wahl === k.key} data-testid={`bericht-kat-${k.key}`} onClick={() => { setWahl(k.key); setErgebnis(null) }}
                className={`min-h-[56px] rounded-xl border p-3 text-left ${wahl === k.key ? 'border-primary-500 bg-primary-500/15' : 'border-dark-border hover:bg-dark-bg-tertiary'}`}>
                <div className="flex items-baseline justify-between gap-2"><span className="font-semibold text-dark-text">{k.label}</span><span className="text-sm text-dark-text">{k.anzahl} Fotos · {mbText(k.bytes)}</span></div>
                <div className="mt-0.5 text-xs text-dark-text-muted">
                  löschbar: {k.loeschbar_anzahl} ({mbText(k.loeschbar_bytes)})
                  {k.laufend_anzahl > 0 && <span className="text-amber-300"> · nicht löschbar, Einsatz nicht abgeschlossen: {k.laufend_anzahl}</span>}
                </div>
              </button>))}
          </div>
          <div>
            <Button variant="secondary" disabled={!kat || kat.loeschbar_anzahl === 0} onClick={oeffnen} data-testid="bericht-loeschen-start" className="min-h-[44px]">
              <AlertCircle className="mr-2 h-4 w-4" />Alle markierten Fotos dieser Kategorie löschen</Button>
            {!kat && <p className="mt-1 text-xs text-dark-text-muted">Zuerst eine Kategorie wählen.</p>}
            {kat && kat.loeschbar_anzahl === 0 && <p className="mt-1 text-xs text-dark-text-muted">In dieser Kategorie ist nichts löschbar.</p>}
          </div>
          <div>
            <h4 className="mb-1 text-sm font-semibold text-dark-text">Letzte Löschungen</h4>
            {u.protokoll.length === 0 ? <p className="text-sm text-dark-text-muted">Noch keine Löschungen.</p> : (
              <ul className="divide-y divide-dark-border rounded-xl border border-dark-border" data-testid="bericht-protokoll">
                {u.protokoll.map(p => (
                  <li key={p.id} className="p-3 text-sm">
                    <div className="text-dark-text">{datumZeit(p.ausgefuehrt_am)} · {p.nutzer_name ?? 'unbekannt'} · {katText(p.kategorie)} · {p.anzahl_geloescht} von {p.anzahl_gewaehlt} gelöscht ({mbText(p.bytes_geloescht)}){p.anzahl_fehler > 0 && <span className="text-red-300"> · {p.anzahl_fehler} Fehler</span>}</div>
                    <div className="break-words text-xs text-dark-text-muted">Foto-IDs: {p.medien_ids.length ? p.medien_ids.join(', ') : '–'}{p.fehler_ids.length > 0 && <> · fehlgeschlagen: {p.fehler_ids.join(', ')}</>}</div>
                  </li>))}
              </ul>)}
          </div>
        </>)}
      {/* per Portal an document.body: sonst bezieht sich „fixed“ auf den (animierten) Seitenbereich und der Dialog liegt außerhalb des Bildschirms */}
      {createPortal(<Dialog isOpen={dialog} onClose={() => !busy && setDialog(false)} title="Markierte Fotos endgültig löschen?" maxWidth="md"
        footer={<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={() => setDialog(false)} disabled={busy} className="min-h-[44px]">Abbrechen</Button>
          <Button onClick={loeschen} disabled={!bestaetigt || busy} data-testid="bericht-loeschen-bestaetigen" className="min-h-[44px] !bg-red-600 hover:!bg-red-700 disabled:opacity-40">{busy ? 'Löscht …' : `${kat?.loeschbar_anzahl ?? 0} Fotos endgültig löschen`}</Button></div>}>
        {kat && (
          <div className="space-y-3 text-sm text-dark-text" data-testid="bericht-dialog">
            <p>Kategorie <strong>{kat.label}</strong>: <strong>{kat.loeschbar_anzahl} Fotos</strong> mit einem Speicherbedarf von <strong>{mbText(kat.loeschbar_bytes)}</strong> werden gelöscht (Original, verkleinerte Fassung, Vorschau, Panorama-Fassungen). Die Markierung wird aufgehoben; Ort, Zeit, Temperaturen und Hotspots bleiben erhalten.</p>
            <p className="font-semibold text-red-300">Das kann nicht rückgängig gemacht werden.</p>
            {kat.laufend_anzahl > 0 && <p className="text-amber-300">{kat.laufend_anzahl} markierte Fotos laufender Einsätze werden nicht gelöscht.</p>}
            <label className="block">
              <span className="label">Zur Bestätigung die Anzahl eintippen ({kat.loeschbar_anzahl})</span>
              <Input inputMode="numeric" autoComplete="off" value={eingabe} onChange={e => setEingabe(e.target.value)} data-testid="bericht-anzahl-eingabe" className="mt-1.5 min-h-[44px]" />
            </label>
            {dialogFehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-300" role="alert">{dialogFehler}</div>}
          </div>)}
      </Dialog>, document.body)}
    </Abschnitt>)
}

/** Einsätze mit Medien, die noch nicht den Status ABGESCHLOSSEN haben: ihre Dateien werden nie frei (Risiko für die Obergrenze) */
function OffeneEinsaetzeAbschnitt() {
  const [liste, setListe] = useState<OffenerEinsatz[] | null>(null)
  useEffect(() => { api.get('/settings/medien/bericht-fotos').then(r => setListe((r.data as BerichtUebersicht).offene_einsaetze)).catch(() => setListe(null)) }, [])
  const gesamt = (liste ?? []).reduce((a, o) => a + o.foto_bytes + o.video_bytes, 0)
  return (
    <Abschnitt titel="Einsätze mit Medien, die noch nicht abgeschlossen sind" beschreibung="Die Löschfrist für Originale und Videos beginnt erst mit dem Status ABGESCHLOSSEN. Dateien dieser Einsätze werden nie frei; bei vollem Medienspeicher können sonst Uploads gesperrt werden. Übungen schließt die Führung in der Einsatzverwaltung ab.">
      {liste && (liste.length === 0 ? <p className="text-sm text-dark-text-muted" data-testid="offene-einsaetze-leer">Keine.</p> : (
        <>
          <p className="text-sm text-dark-text" data-testid="offene-einsaetze-summe">{liste.length} Einsätze, zusammen {mbText(gesamt)}</p>
          <ul className="divide-y divide-dark-border rounded-xl border border-dark-border" data-testid="offene-einsaetze">
            {liste.map(o => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                <span className="min-w-0 break-words text-dark-text">#{o.id} {o.titel ?? '–'} <span className="text-dark-text-muted">({o.typ ?? 'ohne Typ'})</span></span>
                <span className="text-dark-text-muted">Status <strong className="text-dark-text">{o.status ?? '–'}</strong> · {o.fotos} Fotos, {o.videos} Videos · {mbText(o.foto_bytes + o.video_bytes)}</span>
              </li>))}
          </ul>
        </>))}
    </Abschnitt>)
}

const gbText = (b?: number) => ((b ?? 0) / 1024 ** 3).toLocaleString('de-DE', { maximumFractionDigits: 2 })

function MedienTab() {
  const [daten, setDaten] = useState<MedienDaten | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState<string | null>(null)

  const uebernehmen = useCallback((d: MedienDaten) => {
    setDaten(d)
    setForm(Object.fromEntries(Object.entries(d.einstellungen).map(([k, v]) => [k, String(v)])))
  }, [])

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try { uebernehmen((await api.get('/settings/medien')).data as MedienDaten) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Einstellungen konnten nicht geladen werden') }
    finally { setLoading(false) }
  }, [uebernehmen])

  useEffect(() => { laden() }, [laden])

  async function speichern() {
    setSpeichert(true); setFehler(null); setErfolg(null)
    try {
      uebernehmen((await api.put('/settings/medien', form)).data as MedienDaten)
      setErfolg('Gespeichert')
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen') }
    finally { setSpeichert(false) }
  }

  if (loading && !daten) return <div className="text-dark-text-muted">Lade …</div>
  const st = daten?.status ?? {}
  const prozent = st.grenze_bytes ? Math.min(100, Math.round(((st.belegt_bytes ?? 0) / st.grenze_bytes) * 100)) : 0

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-dark-text flex items-center gap-2"><Images className="w-5 h-5" />Fotos &amp; Medien</h2>
        <p className="text-sm text-dark-text-muted mt-1">Fotos aus DJI Pilot 2 (Weitwinkel/Zoom und radiometrische IR-Fotos) für Foto-Lagebild und Hotspots in der Taktischen Karte sowie die Videoaufzeichnung des Livebilds je Flug mit gesicherten Ausschnitten.</p>
      </div>
      {fehler && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{fehler}</div>}
      {erfolg && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300">{erfolg}</div>}

      <div className="space-y-5">
        <Abschnitt titel="Speicherstand" beschreibung="Wird vom Medien-Worker regelmäßig (alle 5 Minuten und nach Änderungen der Fristen) aktualisiert. Die Obergrenze gilt für Fotos und Videos zusammen; gesicherte Ausschnitte werden getrennt gezählt.">
          <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary p-4">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium text-dark-text">{gbText(st.belegt_bytes)} GB von {gbText(st.grenze_bytes || (Number(daten?.einstellungen.medien_grenze_gb) || 40) * 1024 ** 3)} GB belegt</span>
              <span className="text-dark-text-muted">{prozent} %</span>
            </div>
            <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-dark-border">
              <div className={st.voll ? 'bg-red-500' : prozent >= 90 ? 'bg-amber-500' : 'bg-primary-600'} style={{ width: `${st.grenze_bytes ? Math.min(100, ((st.foto_bytes ?? 0) / st.grenze_bytes) * 100) : prozent}%` }} />
              <div className={st.voll ? 'bg-red-400' : prozent >= 90 ? 'bg-amber-400' : 'bg-sky-500'} style={{ width: `${st.grenze_bytes ? Math.min(100, ((st.video_bytes ?? 0) / st.grenze_bytes) * 100) : 0}%` }} />
            </div>
            <div className="mt-3 grid gap-1 text-sm md:grid-cols-3">
              <div className="text-dark-text"><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm bg-primary-600" />Fotos: {gbText(st.foto_bytes)} GB</div>
              <div className="text-dark-text"><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm bg-sky-500" />Videos: {gbText(st.video_bytes)} GB ({st.videos ?? 0} Aufnahmen)</div>
              <div className="text-dark-text">Ausschnitte: {gbText(st.ausschnitt_bytes)} GB ({st.ausschnitte ?? 0}, außerhalb der Obergrenze)</div>
            </div>
            <div className="mt-3 grid gap-1 text-xs text-dark-text-muted md:grid-cols-2">
              <div>Fotos: {st.fotos ?? 0} (davon {st.mit_original ?? 0} mit Original, {st.wartend ?? 0} in Warteschlange, {st.fehler ?? 0} fehlerhaft)</div>
              <div>Freier Platz auf dem Server: {gbText(st.host_frei_bytes)} GB</div>
              <div>Thermal SDK: {st.thermal_sdk === 'ok' ? 'bereit' : (st.thermal_sdk || 'unbekannt')}</div>
              <div>Stand: {st.aktualisiert ? new Date(st.aktualisiert).toLocaleString('de-DE') : 'noch nicht ermittelt'}</div>
            </div>
          </div>
          {(st.warnungen ?? []).map(w => (
            <div key={w.code} className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${w.code === 'voll' ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300'}`}>
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{w.text}
            </div>
          ))}
        </Abschnitt>

        <BerichtFotosAbschnitt onGeaendert={laden} />
        <OffeneEinsaetzeAbschnitt />

        {MEDIEN_FELDER.map(g => (
          <Abschnitt key={g.gruppe} titel={g.gruppe} beschreibung={g.beschreibung}>
            <div className="grid gap-4 md:grid-cols-2">
              {g.felder.map(f => (
                <Feld key={f.key} label={`${f.label} (${daten?.einheiten[f.key] ?? ''})`} hinweis={f.hinweis}>
                  <Input type="number" value={form[f.key] ?? ''} onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}
                    min={daten?.grenzen[f.key]?.[0]} max={daten?.grenzen[f.key]?.[1]} />
                </Feld>
              ))}
            </div>
          </Abschnitt>
        ))}

        <Abschnitt titel="Videoaufzeichnung des Livebilds" beschreibung="MediaMTX zeichnet das Livebild je Flug unverändert auf (keine Neukodierung). Die Aufnahme wird dem Flug und Einsatz zugeordnet und kann in der Flugansicht abgespielt werden.">
          <label className="flex items-start gap-3 text-sm text-dark-text">
            <input type="checkbox" className="mt-1 h-5 w-5" checked={form.live_auto !== 'false'} onChange={e => setForm(v => ({ ...v, live_auto: e.target.checked ? 'true' : 'false' }))} />
            <span>Livebild beim Start automatisch anfordern (für die Aufzeichnung)
              <span className="mt-1 block text-xs text-dark-text-muted">Beim Abheben fordert der Server das Livebild bei der Fernsteuerung an und beendet es nach der Landung, sofern niemand anderes es nutzt. Hinweis: Das verbraucht mobile Daten der Fernsteuerung (je nach Bildqualität etwa 1 bis 3 GB pro Flugstunde). Ist die Einstellung aus, wird nur aufgezeichnet, wenn jemand das Livebild anfordert oder der Pilot es selbst startet.</span>
            </span>
          </label>
          {st.aufzeichnung === false && <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />Die Videoaufzeichnung ist derzeit angehalten (zu wenig freier Platz auf dem Server).</div>}
        </Abschnitt>

        <Abschnitt titel="Panoramen" beschreibung="Panoramen der Matrice 4TD aus DJI Pilot 2 (Kugel 360°, 180°, Weitwinkel, Vertikal): werden beim Upload erkannt und mit einem eigenen Symbol in den Lagekarten angezeigt. Zwei zusätzliche Fassungen (8192 und 4096 px) zählen zur Medien-Obergrenze und folgen der Jahresregel der verkleinerten Fotos.">
          <PanoramenListe />
        </Abschnitt>

        <Abschnitt titel="Gesicherte Ausschnitte" beschreibung="Von Führung, Pilot oder Admin gesicherte Videoausschnitte (30 Sekunden bis 2 Minuten). Sie bleiben, bis jemand sie löscht.">
          <AusschnitteListe onGeaendert={laden} />
        </Abschnitt>

        <Abschnitt titel="Regeln" beschreibung="Gelten automatisch (täglich um 00:00 Uhr, beim Start und alle 5 Minuten geprüft).">
          <ul className="list-disc space-y-2 pl-5 text-sm text-dark-text-muted">
            {(daten?.regeln ?? []).map(r => <li key={r}>{r}</li>)}
          </ul>
        </Abschnitt>

        <Abschnitt titel="Bildgrößen und Pilot 2" beschreibung="Fest eingestellt, nicht veränderbar.">
          <ul className="list-disc space-y-1 pl-5 text-sm text-dark-text-muted">
            <li>Weitwinkel/Zoom: verkleinerte Fassung mit 2560 px langer Kante (JPEG, Qualität 80), Vorschau 400 px.</li>
            <li>Panoramen: zusätzlich eine Fassung mit 8192 px (nur wenn das Original größer ist) und eine mit 4096 px für iPad/Handy (JPEG, Qualität 82).</li>
            <li>IR: Vorschau 400 px und unverändertes Bild 640 × 512 als verkleinerte Fassung; Temperaturen aus dem Original (DJI Thermal SDK).</li>
            <li>DJI Pilot 2 lädt beim Verbinden automatisch Fotos als Original hoch; Video-Upload aus Pilot 2 ist ausgeschaltet (Videos entstehen nur aus der Aufzeichnung des Livebilds).</li>
          </ul>
        </Abschnitt>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="secondary" onClick={laden} disabled={loading}><RefreshCw className="w-4 h-4 mr-2" />Aktualisieren</Button>
        <Button onClick={speichern} disabled={speichert}><Save className="w-4 h-4 mr-2" />{speichert ? 'Speichern…' : 'Speichern'}</Button>
      </div>
    </>
  )
}

export function SettingsPage() {
  const [tab, setTab] = useState<'profil' | 'system' | 'medien'>('profil')

  const tabs = [
    { id: 'profil', label: 'Mein Profil', icon: User },
    { id: 'system', label: 'System', icon: SettingsIcon },
    { id: 'medien', label: 'Fotos & Medien', icon: Images },
  ] as const

  return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Eigene Kontodaten verwalten und System konfigurieren" />

      {/* Tab Navigation - Button Style wie Führungs-Modul */}
      <div className="flex gap-2">
        {tabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${tab === t.id ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/25' : 'bg-dark-bg-tertiary text-dark-text-muted hover:bg-dark-border hover:text-dark-text'}`}>
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          )
        })}
      </div>

      <Card className="p-0">
        <div className="p-6">
          {tab === 'profil' && <ProfilTab />}
          {tab === 'system' && <SystemTab />}
          {tab === 'medien' && <MedienTab />}
        </div>
      </Card>
    </div>
  )
}